import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Media from '../../models/Media';
import Dropzone, { IFileWithMeta, StatusValue } from 'react-dropzone-uploader';
import MediaService from "../../services/use_case/media/MediaService";
import ApiResponse from '../../services/ApiResponse';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faPlus } from '@fortawesome/free-solid-svg-icons';
import {
    DropzoneItem,
    DropzoneLayout,
} from "../index";

interface CreateLibraryProps {
    disableOnClose?: (value: boolean) => void;
    handleNewMedia?: (media: Media) => void;
};

const CreateLibrary: React.FC<CreateLibraryProps> = (props: CreateLibraryProps) => {

    const [t] = useTranslation();
    const mediaService: MediaService = new MediaService();
    const dropzoneEl = useRef<any>();

    useEffect(() => {

        const dropzone = dropzoneEl.current;
        const dzu = dropzone.querySelector(".dzu-dropzone");

        dzu.addEventListener('dragover', _onDragOver);
        dzu.addEventListener('dragleave', _onDragLeave);

        return () => {
            dzu.removeEventListener('dragover', _onDragOver);
            dzu.removeEventListener('dragleave', _onDragLeave);
        }

    }, []);

    const _onDragOver = () => {
        dropzoneEl.current.querySelector(".dzu-dropzone").classList.add("dzu-dropzone--active");
    }

    const _onDragLeave = () => {
        dropzoneEl.current.querySelector(".dzu-dropzone").classList.remove("dzu-dropzone--active");
    }

    const _getUploadParams = (file: IFileWithMeta) => {

        const body = new FormData();
        body.append('file', file.file);
        body.append('name', file.meta.name);

        const lang = localStorage.getItem("i18nextLng")?.split('-')[0] ?? "en";

        return {
            url: `${mediaService.baseURL}/medias`,
            headers: {
                "Authorization": `Bearer ${mediaService.token}`,
                'Access-Control-Allow-Origin': '*',
                'Accept-Language': lang,
            },
            body,
        }
    }

    const _handleChangeStatus = (file: IFileWithMeta, status: StatusValue) => {
        if (status === "done" && file.meta.percent === 100) {
            try {
                const response: ApiResponse<Media> = JSON.parse(file.xhr?.response ?? "");
                if (response.data) props.handleNewMedia?.(response.data);
            }
            catch (_) {}
        }
    }

    const inputContent = (
        <div className="dropzone-input-content">
            <FontAwesomeIcon
                icon={faUpload}
                size="3x"
                className="dropzone-input-content__icon"
            />
            <p className="dropzone-input-content__text">{t('dropzone_text')}</p>
            <span className="dropzone-input-content__formats">{t('dropzone_formats')}</span>
        </div>
    );

    const inputWithFilesContent = (
        <div className="dropzone-input-add">
            <FontAwesomeIcon icon={faPlus} />
            <span className="dropzone-input-add__label">{t('dropzone_add_more')}</span>
        </div>
    );

    return (
        <div className="dropzone" ref={dropzoneEl}>
            <Dropzone
                accept="image/*,video/*"
                getUploadParams={_getUploadParams}
                PreviewComponent={DropzoneItem}
                onChangeStatus={_handleChangeStatus}
                multiple={true}
                autoUpload={true}
                SubmitButtonComponent={() => null}
                inputContent={inputContent}
                inputWithFilesContent={inputWithFilesContent}
                LayoutComponent={DropzoneLayout}
                styles={{
                    dropzone: {
                        minHeight: '40vh',
                        border: '1px dashed rgba(150, 150, 150, 0.2)',
                    },
                    dropzoneActive: {
                        backgroundColor: 'transparent',
                    },
                }}
            />
        </div>
    );

};

export default React.memo(CreateLibrary);
